/**
 * PromotionDialog Component
 * 
 * Displays options for pawn promotion when a pawn reaches the opposite rank.
 */

import React, { useEffect, useRef } from 'react';
import ChessPiece from '../ChessPiece';
import { PieceType, PieceColor } from '../../../types/chess';
import styles from './PromotionDialog.module.css';

export interface PromotionDialogProps {
  /** The color of the player's pawn being promoted */
  color: PieceColor;
  /** Callback when a promotion piece is selected */
  onSelect: (pieceType: PieceType) => void;
  /** Callback when the promotion is canceled */
  onCancel: () => void;
  /** The position of the dialog, defaulting to center */
  position?: {
    top?: number;
    left?: number;
    right?: number;
    bottom?: number;
  };
  /** Available promotion pieces (defaults to standard q,r,b,n) */
  availablePieces?: PieceType[];
}

/**
 * PromotionDialog - Displays piece options when a pawn is being promoted
 * 
 * This component shows a modal dialog with available promotion pieces
 * when a pawn reaches the opposite rank.
 */
const PromotionDialog: React.FC<PromotionDialogProps> = ({
  color,
  onSelect,
  onCancel,
  position,
  availablePieces = ['q', 'r', 'b', 'n']
}) => {
  // Refs for dialog and first piece button
  const dialogRef = useRef<HTMLDivElement>(null);
  const firstPieceRef = useRef<HTMLButtonElement>(null);

  /**
   * Get the piece name for accessibility
   */
  const getPieceName = (piece: PieceType): string => {
    const pieceNames = {
      q: 'Queen',
      r: 'Rook',
      b: 'Bishop',
      n: 'Knight',
      p: 'Pawn',
      k: 'King'
    };
    return pieceNames[piece];
  };

  /**
   * Handle the selection of a promotion piece
   */
  const handleSelect = (piece: PieceType) => {
    onSelect(piece);
  };

  /**
   * Handle keyboard navigation between piece buttons
   */
  const handleKeyDown = (e: React.KeyboardEvent) => {
    const buttons = Array.from(
      dialogRef.current?.querySelectorAll('[data-testid^="promote-to-"]') || []
    ) as HTMLButtonElement[];

    if (!buttons.length) return;

    const currentIndex = buttons.findIndex(btn => btn === document.activeElement);
    let nextIndex = currentIndex;

    switch (e.key) {
      case 'ArrowRight':
      case 'ArrowDown':
        nextIndex = (currentIndex + 1) % buttons.length;
        e.preventDefault();
        break;
      case 'ArrowLeft':
      case 'ArrowUp':
        nextIndex = (currentIndex - 1 + buttons.length) % buttons.length;
        e.preventDefault();
        break;
      case 'Escape':
        onCancel();
        e.preventDefault();
        break;
      case 'Enter':
      case ' ':
        // Let the button handle it
        break;
      default:
        break;
    }

    if (nextIndex !== currentIndex) {
      buttons[nextIndex].focus();
    }
  };

  // Set focus to the first promotion piece button when dialog opens
  useEffect(() => {
    if (firstPieceRef.current) {
      setTimeout(() => {
        firstPieceRef.current?.focus();
      }, 100);
    }

    // Track document focus for better accessibility
    const handleFocusOutside = (e: FocusEvent) => {
      if (
        dialogRef.current &&
        e.target &&
        !dialogRef.current.contains(e.target as Node)
      ) {
        firstPieceRef.current?.focus();
      }
    };

    document.addEventListener('focusin', handleFocusOutside);
    
    // Lock scroll when dialog is open
    const originalStyle = window.getComputedStyle(document.body).overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('focusin', handleFocusOutside);
      document.body.style.overflow = originalStyle;
    };
  }, []);

  // Custom position styles if provided
  const positionStyle = position ? {
    top: position.top !== undefined ? `${position.top}px` : undefined,
    left: position.left !== undefined ? `${position.left}px` : undefined,
    right: position.right !== undefined ? `${position.right}px` : undefined,
    bottom: position.bottom !== undefined ? `${position.bottom}px` : undefined,
  } : {};

  return (
    <div 
      className={styles.overlay} 
      onClick={onCancel} 
      data-testid="promotion-dialog-overlay"
      role="presentation"
    >
      <div 
        ref={dialogRef}
        className={styles.dialog} 
        style={positionStyle}
        onClick={(e) => e.stopPropagation()}
        data-testid="promotion-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="promotion-dialog-title"
        onKeyDown={handleKeyDown}
      >
        <div className={styles.dialogHeader} id="promotion-dialog-title">
          <h3>Promote Pawn</h3>
          <button 
            className={styles.closeButton} 
            onClick={onCancel}
            aria-label="Close promotion dialog"
          >
            ×
          </button>
        </div>
        
        <div className={styles.dialogContent}>
          <p>Select a piece to promote your pawn:</p>
          <div className={styles.pieceSelection}>
            {availablePieces.map((piece, index) => (
              <button
                key={piece}
                ref={index === 0 ? firstPieceRef : undefined}
                className={styles.pieceButton}
                onClick={() => handleSelect(piece)}
                aria-label={`Promote to ${getPieceName(piece)}`}
                data-testid={`promote-to-${piece}`}
              >
                <span className={styles.pieceName}>{getPieceName(piece)}</span>
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PromotionDialog; 